import { NotificationsBaseDefault, Notifications, NotificationScheduleTimeUnit } from "./comEnvironmentNotifications";
import { CoreLogService } from "../log.service";
import { NotificationsService } from "angular2-notifications";
import { CoreLanguageService } from "../language.service";
import { Client } from "./comEnvironmentClient";
import { Observable } from "rxjs";

/** Notifications Cordovaimplementierung */
export class NotificationsCordova extends NotificationsBaseDefault implements Notifications {

  /** Cordovaplugin for notification */
  private _cordovaNotification: any;

  /** Notifications Cordovaimplementierung
   * @param _notificationsService Notificationsservice
   * @param _coreLanguageService CoreLanuageService
   * @param _client Client
   */
  constructor(protected _notificationsService: NotificationsService,
    protected _coreLanguageService: CoreLanguageService,
    protected _client: Client,
    protected _coreLogService: CoreLogService) {
    super(_notificationsService, _coreLanguageService, _client, _coreLogService);
    this._cordovaNotification = cordova.plugins["notification"];
  }

  /** Globale Nachricht welche Systemweit angezeigt wird (z.B in der Android / IOS Notificationarea)
   * @param titleKey Sprachschlüssel für den Titel
   * @param textKey Sprachschlüssel für den Text
   * @param passthrough Die übergebenen Schlüssel werden nicht über den CoreLanguageService übersetzt wenn der Wert true ist
   */
  public global(titleKey: string, textKey: string, passthrough: boolean = false) {
    try {
      let title = titleKey;
      let text = textKey;
      if (!passthrough) {
        title = this._coreLanguageService.value(title);
        text = this._coreLanguageService.value(text);
      }

      this._cordovaNotification.local.schedule({
        title: title,
        text: text,
        foreground: true,
        smallIcon: "res://logo"
      });

    } catch (ex) {
      this._coreLogService.error("comEnvironmentNotifications.NotificationsCordova.global: Die Meldung konnte nicht ausgegeben werden", ex);
    }
  }

  /** Globale Nachricht welche Systemweit angezeigt wird und die Möglichkeit bietet benutzerdefinierte Daten mitzugeben
  * @param titleKey Sprachschlüssel für den Titel
  * @param textKey Sprachschlüssel für den Text
  * @param customData Benutzerdefinierte Daten
  * @param passthrough Die übergebenen Schlüssel werden nicht über den CoreLanguageService übersetzt wenn der Wert true ist
  */
  public globalCustomData(titleKey: string, textKey: string, customData?: Object, passthrough?: boolean) {
    try {
      let title = titleKey;
      let text = textKey;
      if (!passthrough) {
        title = this._coreLanguageService.value(title);
        text = this._coreLanguageService.value(text);
      }
      if (!customData) {
        customData = {};
      }
      this._cordovaNotification.local.schedule({
        title: title,
        text: text,
        foreground: true,
        data: customData,
        smallIcon: "res://logo"
      });

    } catch (ex) {
      this._coreLogService.error("comEnvironmentNotifications.NotificationsCordova.global: Die Meldung konnte nicht ausgegeben werden", ex);
    }
  }

  private _globalScheduleTriggerCordova(scheduleID: number, trigger: any, titleKey: string, textKey: string, passthrough: boolean = false) {
    try {
      let title = titleKey;
      let text = textKey;
      if (!passthrough) {
        title = this._coreLanguageService.value(title);
        text = this._coreLanguageService.value(text);
      }

      this._cordovaNotification.local.schedule({
        id: scheduleID,
        title: title,
        text: text,
        foreground: true,
        trigger: trigger,
        smallIcon: "res://logo"
      });

    } catch (ex) {
      this._coreLogService.error("comEnvironmentNotifications.NotificationsCordova._globalScheduleTriggerCordova: Der Trigger konnte nicht gesetzt werden", ex);
    }
  }

  /** Löst eine globale Nachricht in der angegebenen Zeit aus
   * @param scheduleID ScheduleID welche frei vergeben werden kann aber eindeutig sein muss
   * @param scheduleIn Anzahl von Zeiteinheiten in der die Nachricht angezeigt werden soll
   * @param unit Zeiteinheit
   * @param titleKey Sprachschlüssel für den Titel
   * @param textKey Sprachschlüssel für den Text
   * @param passthrough Die übergebenen Schlüssel werden nicht über den CoreLanguageService übersetzt wenn der Wert true ist
   */
  public globalScheduleIn(scheduleID: number, scheduleIn: number, unit: NotificationScheduleTimeUnit, titleKey: string, textKey: string, passthrough: boolean = false) {

    try {
      this._globalScheduleTriggerCordova(scheduleID, { in: scheduleIn, unit: unit }, titleKey, textKey, passthrough);
    } catch (ex) {
      this._coreLogService.error("comEnvironmentNotifications.NotificationsCordova.globalScheduleIn: Der Trigger konnte nicht gesetzt werden", ex);
    }
  }

  /** Löst eine globale Nachricht in Abständen der angegebenen Zeiteinheit aus
 * @param scheduleID ScheduleID welche frei vergeben werden kann aber eindeutig sein muss
 * @param scheduleIn Anzahl von Zeiteinheiten in der die Nachricht angezeigt werden soll
 * @param unit Zeiteinheit
 * @param titleKey Sprachschlüssel für den Titel
 * @param textKey Sprachschlüssel für den Text
 * @param passthrough Die übergebenen Schlüssel werden nicht über den CoreLanguageService übersetzt wenn der Wert true ist
 */
  public globalScheduleEveryUnit(scheduleID: number, unit: NotificationScheduleTimeUnit, titleKey: string, textKey: string, passthrough: boolean = false): void {
    try {
      this._globalScheduleTriggerCordova(scheduleID, { every: unit }, titleKey, textKey, passthrough);
    } catch (ex) {
      this._coreLogService.error("comEnvironmentNotifications.NotificationsCordova.globalScheduleEveryUnit: Der Trigger konnte nicht gesetzt werden", ex);
    }
  }

  /** Löst eine globale Nachricht in Abständen der angegebenen Zeiteinheit aus wenn die Methode nicht erneut mit der identischen ID aufegrufen wird
  * @param scheduleID ScheduleID welche frei vergeben werden kann aber eindeutig sein muss
  * @param unit Zeiteinheit
  * @param titleKey Sprachschlüssel für den Titel
  * @param textKey Sprachschlüssel für den Text
  * @param passthrough Die übergebenen Schlüssel werden nicht über den CoreLanguageService übersetzt wenn der Wert true ist
  */
  public async globalScheduleOnInactivityEveryUnit(scheduleID: number, unit: NotificationScheduleTimeUnit, titleKey: string, textKey: string, passthrough: boolean = false): Promise<void> {
    try {
      const scheduleIDs: Array<number> = await this.globalScheduleGetIDs();
      if (scheduleIDs.indexOf(scheduleID) > -1) {
        await this.globalScheduleCancel(scheduleID);
      }

      this.globalScheduleEveryUnit(scheduleID, unit, titleKey, textKey, passthrough);
    } catch (ex) {
      this._coreLogService.error("comEnvironmentNotifications.NotificationsCordova.globalScheduleOnInactivityEveryUnit: Der Trigger konnte nicht gesetzt werden", ex);
    }
  }

  /** Gibt die aktuell registrierten ScheduleIDs zurück */
  public globalScheduleGetIDs(): Promise<Array<number>> {
    return new Promise<Array<number>>((resolve, reject) => {
      try {
        this._cordovaNotification.local.getIds(resolve);
      } catch (ex) {
        this._coreLogService.error("comEnvironmentNotifications.NotificationsCordova.globalScheduleGetIDs: Die NotificationtaskIDs konnten nicht gelesen werden", ex);
        reject(ex);
      }
    });
  }

  /** Löscht einen Notificationtask anhand der übergebenen ScheduleID
   * @param scheduleID ScheduleID der Notification
   */
  public globalScheduleCancel(scheduleID: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      try {
        this._cordovaNotification.local.cancel(scheduleID, resolve);
      } catch (ex) {
        this._coreLogService.error("comEnvironmentNotifications.NotificationsCordova.globalScheduleClear: Der Notificationtask konnte nicht entfernt werden", ex);
        reject(ex);
      }
    });
  }

  /** Gibt ein Observable zurück, das mit einer Notification feuert, wenn eine Systemweite LOKALE Push Notification von dem Benutzer aktiviert wird.
   * ! Wenn das FCMPlugin aktiv ist, funktioniert dieser Observer nur auf Android. 
   * ! Andernfalls werden die PNs von dem FCMPlugin abgefangen. (s. für iOS comEnviromentFirebaseCloudMessaging.mapToIFCMNotificationData)
   * @return Observable, das ein PushNachrichten als Object ausgibt. ({title: string, text:string, data:{}, ...})
   */
  public observeClick(): Observable<Object> {
    const me = this;
    return new Observable<Object>(subscriber => {
      const subscribeFunc = (notification) => {
        subscriber.next(notification);
      };
      me._cordovaNotification.local.on("click", subscribeFunc, me);
      if (me._cordovaNotification.local.fireQueuedEvents) {
        // It might be possible that the underlying framework like Ionic is not compatible with the launch process defined by cordova.
        // With the result that the plugin fires the click event on app start before the app is able to listen for the events.
        // Once the app and cordova is ready, you can fire the queued events manually.
        me._cordovaNotification.local.fireQueuedEvents();
      }
      return () => { // Unsubscribe
        me._cordovaNotification.local.un("click", subscribeFunc, me);
      };
    });
  }
}
